import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../../store/revisione53.reducers';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { toNum } from '@shared/helpers/math.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { cloneDeep } from 'lodash-es';
import { MyVerbale } from '../../../../models/interfaces/revisione.interface';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { formPaths } from '@shared/constants/form-paths.const';

const subscribeSociControlsWithInitialValue = (form: FormGroup, callback: () => void): void => {
    const effGroup = getGroup(form, formPaths.baseSociale);
    const soci1 = getControl(effGroup, 'my_SociFisCoopData');
    const soci2 = getControl(effGroup, 'my_SociGiuCoopData');
    const soci3 = getControl(effGroup, 'my_SociSpecData');

    merge(soci1.valueChanges.pipe(startWith(soci1.value)), soci2.valueChanges.pipe(startWith(soci2.value)), soci3.valueChanges.pipe(startWith(soci3.value)))
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(callback);
};

const sommaCoopSoci = (form: FormGroup): number => {
    const effGroup = getGroup(form, formPaths.baseSociale);
    return (
        toNum(getControl(effGroup, 'my_SociFisCoopData')?.value) + toNum(getControl(effGroup, 'my_SociGiuCoopData')?.value) + toNum(getControl(effGroup, 'my_SociSpecData')?.value)
    );
};

/** Shared logic for all coop9soci listeners. `inverse=true` means ≥9 → '0' (SRL case). */
const coop9SociListener = (form: FormGroup, store: Store<Revisione53State>, ckPath: string, storeSetter: (clone: MyVerbale, value: '0' | '1') => void, inverse = false): void => {
    const ckControl = getControl(form, ckPath);
    subscribeSociControlsWithInitialValue(form, () => {
        const value: '0' | '1' = sommaCoopSoci(form) >= 9 !== inverse ? '1' : '0';
        ckControl.patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const clone: MyVerbale = cloneDeep(myVerbale);
            storeSetter(clone, value);
            return clone;
        });
    });
};

/** Listens to cooperative soci data and updates my_CkSpdCoop9soci for NormeSpaDualistico. */
export const my_SpdCoop9SociListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void =>
    coop9SociListener(form, store, `${formPaths.ammControllo}.my_NormeSpaDualistico.my_CkSpdCoop9soci`, (clone, v) => {
        clone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaDualistico.my_CkSpdCoop9soci = v;
    });

/** Listens to cooperative soci data and updates my_CkSpoCoop9soci for NormeSpaOrdinario. */
export const my_SpoCoop9SociListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void =>
    coop9SociListener(form, store, `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoCoop9soci`, (clone, v) => {
        clone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaOrdinario.my_CkSpoCoop9soci = v;
    });

/** Listens to cooperative soci data and updates my_CkSpmCoop9soci for NormeSpaMonistico. */
export const my_SpmCoop9SociListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void =>
    coop9SociListener(form, store, `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmCoop9soci`, (clone, v) => {
        clone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaMonistico.my_CkSpmCoop9soci = v;
    });

/** Listens to cooperative soci data and updates my_CkSrlCoop9soci for NormeSrl (inverse: < 9 soci → '1'). */
export const my_SrlCoop9SociListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void =>
    coop9SociListener(
        form,
        store,
        `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop9soci`,
        (clone, v) => {
            clone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlCoop9soci = v;
        },
        true
    );

/** Listens to cooperative soci data and patrimonio to update my_CkSrlCoop20soci for NormeSrl. */
export const my_SrlCoop20SociListenerHelper = (form: FormGroup, store: Store<Revisione53State>): void => {
    const effettivaBaseSocialeGroup = getGroup(form, formPaths.baseSociale);
    const sociFisiciCoopControl = getControl(effettivaBaseSocialeGroup, 'my_SociFisCoopData');
    const sociGiuridiciCoopControl = getControl(effettivaBaseSocialeGroup, 'my_SociGiuCoopData');
    const sociSpecialiControl = getControl(effettivaBaseSocialeGroup, 'my_SociSpecData');
    const totaleAttivoAnno2Control = getControl(form, `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`);
    const ckSrlCoop20SociControl = getControl(form, `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop20soci`);

    merge(
        sociFisiciCoopControl.valueChanges.pipe(startWith(sociFisiciCoopControl.value)),
        sociGiuridiciCoopControl.valueChanges.pipe(startWith(sociGiuridiciCoopControl.value)),
        sociSpecialiControl.valueChanges.pipe(startWith(sociSpecialiControl.value)),
        totaleAttivoAnno2Control.valueChanges.pipe(startWith(totaleAttivoAnno2Control.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const numeroSociCoop = sommaCoopSoci(form);
            const totaleAttivoAnno2 = isNilOrEmpty(totaleAttivoAnno2Control.value) ? 2_000_000 : toNum(totaleAttivoAnno2Control.value);
            const valoreCkSrlCoop20Soci: '0' | '1' = numeroSociCoop < 20 || totaleAttivoAnno2 < 1_438_000 ? '1' : '0';

            ckSrlCoop20SociControl.patchValue(valoreCkSrlCoop20Soci, {
                emitEvent: false,
            });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlCoop20soci = valoreCkSrlCoop20Soci;
                return myVerbaleClone;
            });
        });
};
